import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { CustomersFacade } from '@domain/application/facade';
import { SessionService } from '@domain/application/observable-services/session.service';
import { onOutsideTaxUserInput } from '@modules/business-accounts/forms/outside-tax/outside-tax.form';
// import {
//   appendCountryTaxDetailForm,
//   createCountryTaxDetailForm,
//   getCountryTaxDetailFormInstance,
//   makeTaxNumberNonRequired,
//   makeTaxNumberRequired,
//   onCountryTaxRequirementChange,
//   removeCountryTaxDetailForm,
// } from '@modules/business-accounts/forms/tax-country-detail-form/tax-country-detail.form';
import {
  getPersonalDataClientFormInstance,
  getSectorDataClientFormInstance,
} from '@modules/business-accounts/forms/utils/data-step/natural-person/builders/natural-person-data-client-builder.class';
import {
  getMiscDataNonClientFormInstance,
  getPersonalDataNonClientFormInstance,
  getSectorDataNonClientFormInstance,
} from '@modules/business-accounts/forms/utils/data-step/natural-person/builders/natural-person-data-non-client-builder.class';
import { ParametersService } from '@modules/business-accounts/services/parameters/parameters.service';
import { formatPhoneNumber } from '@modules/business-accounts/utils/classes/string';
import { NullableObservable } from '@modules/business-accounts/utils/types/observable.type';
import { dateRangeValidator, setMaxDatePicker } from '@utils/functions/datepicker';
import { CustomerReniecResponse } from '@utils/interfaces/response';
import { Unsubscriber } from '@utils/mixins/unsubscribe.mixin';
import { Observable, of, startWith, switchMap, tap } from 'rxjs';
import { IStep } from '../../utils/interfaces/step.interface';
import { VisitEvent } from '@modules/business-accounts/utils/classes/visit-event.class';
import { TraceEventName } from '@modules/business-accounts/utils/enums/trace-events.enum';
import { TraceService } from '@modules/business-accounts/services/trace/trace.service';

export interface SubRubro {
  value: string;
  label: string;
}
export interface Rubro {
  value: string;
  label: string;
  isForbidden?: boolean;
  items?: SubRubro[];
}

@Component({
  selector: 'ba-natural-person-data-step',
  templateUrl: './natural-person-data-step.component.html',
  styleUrls: ['./natural-person-data-step.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class NaturalPersonDataStepComponent extends Unsubscriber() implements OnInit, IStep<void> {
  @Input() isClient: boolean = false;
  @Input() documentNumber!: string;

  @Output() stepDone = new EventEmitter<void>();

  showContinueDisclaimer: boolean = false;
  showErrorDisclaimer: boolean = false;
  showOutsideTax: boolean = false;
  ibkConfig = setMaxDatePicker(18);

  department$!: Observable<any>;
  provinces$!: NullableObservable<ReturnType<ParametersService['getProvinces']>>;
  districts$!: NullableObservable<ReturnType<ParametersService['getDistricts']>>;
  customer$!: Observable<CustomerReniecResponse>;
  havePhone = false;
  haveEmail = false;

  list = {
    departments: [],
    provinces: [],
    districts: [],
  };

  constructor(
    public readonly paramsService: ParametersService,
    private readonly controlContainer: ControlContainer,
    private readonly customersFacade: CustomersFacade,
    private readonly sessionService: SessionService,
    private readonly traceService: TraceService
  ) {
    super();
    const { personType } = this.sessionService.getCommonInfoValue;
    if (personType == undefined) this.showOutsideTax = true;
  }

  rubros: Array<{ value: string; label: string }> = [
    { value: '01', label: 'Alquileres' },
    { value: '02', label: 'Servicios agrícolas' },
    { value: '03', label: 'Servicios comerciales' },
    { value: '04', label: 'Servicio de transporte, carga y/o logística' },
    { value: '05', label: 'Servicios y/o ventas minoristas' },
    { value: '06', label: 'Servicios varios' },
    { value: '07', label: 'Otros Rubros' },
  ];

  subrubros: Array<{ value: string; label: string }> = [
    { value: '0301', label: 'Bazar y/o juguetería' },
    { value: '0302', label: 'Consultoría y/o Asesorías generales' },
    { value: '0303', label: 'Entretenimiento' },
    { value: '0304', label: 'Joyería' },
    { value: '0305', label: 'Mantenimiento y reparaciones' },
    { value: '0306', label: 'Otros' },
    { value: '0501', label: 'Automotriz' },
    { value: '0502', label: 'Distribución, suministros ó mercancías' },
    { value: '0503', label: 'Ferreterías' },
    { value: '0504', label: 'Tiendas y/o bodegas' },
    { value: '0505', label: 'Otros' },
    { value: '0601', label: 'Actividades deportivas' },
    { value: '0602', label: 'Proveedores, mercancía u otros' },
    { value: '0603', label: 'Viajes y/o turismo' },
    { value: '0604', label: 'Otros' },
  ];

  subrubrosList: never[] | any[] = [];

  setSectorCode(sectorCode: string) {
    if (!sectorCode) return;

    const subrubros = this.subrubros.filter((sb) => sb.value.slice(0, 2) === sectorCode);
    this.subrubrosList = subrubros;
    if (subrubros.length === 0) {
      this.sectorDataForm?.get('subSectorCode')?.removeValidators(Validators.required);
      return;
    }
    this.sectorDataForm?.get('subSectorCode')?.setValue(null);
    this.sectorDataForm?.get('subSectorCode')?.addValidators(Validators.required);
  }

  ngOnInit(): void {
    this.paramsService
      .params$('departments')
      .pipe(
        tap((res) => {
          this.list.departments = res as any;
        })
      )
      .subscribe();

    this.fetchReniecData();
    if (this.showOutsideTax)
      this.listenToForeignTaxForm();
    if (!this.isClient) {
      this.listenToDepartmentChange();
      this.listenToProvinceChange();
      this.listenToDistrictChange();
    }

    this.setSectorCode(this.sectorDataForm.get('sectorCode')?.value);
    this.form
      .get('sectorData')
      ?.get('sectorCode')
      ?.valueChanges.subscribe((sectorCode) => this.setSectorCode(sectorCode));

    if (!this.isClient) {
      this.personalDataForm.get('province')?.valueChanges.subscribe((province) => {
        this.miscDataForm.get('provinceId')?.setValue(province);
      });
      this.personalDataForm.get('district')?.valueChanges.subscribe((district) => {
        this.miscDataForm.get('districtId')?.setValue(district);
      });
      this.personalDataForm.get('department')?.valueChanges.subscribe((department) => {
        this.miscDataForm.get('departmentId')?.setValue(department);
      });
    }
  }

  fetchReniecData() {
    this.customer$ = this.customersFacade.fetchReniecData(this.documentNumber).pipe(
      tap(({ customer }: CustomerReniecResponse) => {
        const mobilePhone = customer?.phone?.find((phone) => phone.type === 'MOBILE');
        if (mobilePhone) {
          this.form.get('personalData')?.get('phoneNumber')?.setValue(formatPhoneNumber(mobilePhone.number));
          this.havePhone = true;
        }
        if (customer.email && customer.email.length > 0) {
          this.form.get('personalData')?.get('email')?.setValue(customer.email[0].value);
          // this.haveEmail = true;
          this.form.get('personalData')?.get('email')?.disable();
        }
        this.sessionService.setCommonInfo = {
          firstName: customer.firstName,
          lastName: customer.lastName
        };
      })
    );
  }

  get form() {
    return (this.controlContainer as FormGroupDirective).form;
  }

  listenToDepartmentChange() {
    let { department } = this.personalDataForm.value;

    this.provinces$ = this.personalDataForm.get('department')!.valueChanges.pipe(
      startWith(this.personalDataForm.get('department')?.value),
      switchMap((res) => {
        const departmentName: any = this.list.departments.find((d: any) => d.value === res);
        if (departmentName) {
          this.personalDataForm.get('departmentName')?.setValue(departmentName.label);
        }
        return res ? this.paramsService.getProvinces(res) : of(null);
      }),
      tap((res) => {
        this.list.provinces = res as any;
        if (!department) {
          this.personalDataForm.get('province')?.setValue(null);
          this.personalDataForm.get('district')?.setValue(null);
          this.personalDataForm.get('provinceName')?.setValue(null);
          this.personalDataForm.get('districtName')?.setValue(null);
        } else {
          department = null;
        }
      })
    );
  }

  listenToProvinceChange() {
    let { department, province } = this.personalDataForm.value;

    this.districts$ = this.personalDataForm.get('province')!.valueChanges.pipe(
      startWith(this.personalDataForm.get('province')?.value),
      switchMap((res) => {
        if (!res) return of(null);
        const { department } = this.personalDataForm.value;
        const provinceName: any = this.list.provinces?.find((d: any) => d.value === res);

        if (provinceName) this.personalDataForm.get('provinceName')?.setValue(provinceName.label);
        return this.paramsService.getDistricts(department, res);
      }),
      tap((res) => {
        this.list.districts = res as any;
        if (!department && !province) {
          this.personalDataForm.get('district')?.setValue(null);
        } else {
          department = null;
          province = null;
        }
      })
    );
  }

  listenToDistrictChange() {
    this.personalDataForm.get('district')?.valueChanges.subscribe((res) => {
      const districtName: any = this.list.districts?.find((d: any) => d.value === res);
      if (districtName) this.personalDataForm.get('districtName')?.setValue(districtName.label);
    });
  }

  listenToForeignTaxForm() {
    this.subsink.sink = onOutsideTaxUserInput({
      on: this.form,
      affirmative: () => {
        this.showContinueDisclaimer = true;
        this.showErrorDisclaimer = false;
        // removeCountryTaxDetailForm({ from: this.form })
      },
      negative: () => {
        this.showContinueDisclaimer = false;
        this.showErrorDisclaimer = true;
        // const countryDetailForm = getCountryTaxDetailFormInstance({ from: this.form }) ?? createCountryTaxDetailForm();
        // appendCountryTaxDetailForm({
        //   to: this.form,
        //   with: countryDetailForm,
        // });
        // this.subsink.sink = onCountryTaxRequirementChange({
        //   on: this.form,
        //   affirmative: () => makeTaxNumberNonRequired({ on: countryDetailForm }),
        //   negative: () => makeTaxNumberRequired({ on: countryDetailForm }),
        // });
      }
      // ,any: (val) => val && (this.showContinueDisclaimer = true),
    });
  }

  done() {
    if (!this.isClient)
      this.sessionService.setCommonInfo = { email: this.form.get('personalData')?.get('email')?.value };
    this.stepDone.emit();
    this.traceService.registerEvent(new VisitEvent(TraceEventName.ACTION_PERSONAL_DATA));
  }

  // get formHasCountryTaxForm(): boolean {
  //   return !!getCountryTaxDetailFormInstance({ from: this.form });
  // }

  get miscDataForm() {
    return getMiscDataNonClientFormInstance({ from: this.form });
  }

  get personalDataForm() {
    return this.isClient
      ? getPersonalDataClientFormInstance({ from: this.form })
      : getPersonalDataNonClientFormInstance({ from: this.form });
  }

  get hasPersonalDataForm(): boolean {
    return !!this.personalDataForm;
  }

  get sectorDataForm() {
    return this.isClient
      ? getSectorDataClientFormInstance({ from: this.form })
      : getSectorDataNonClientFormInstance({ from: this.form });
  }

  get hasSectorDataForm(): boolean {
    return !!this.sectorDataForm;
  }

  chkPep(event: any) {
    const isChecked = event.target.checked;
    const partner = this.personalDataForm as FormGroup;
    if (isChecked) {
      partner.addControl(
        'pepLegalRepresentative',
        new FormGroup({
          pepEntity: new FormControl(null),
          pepPosition: new FormControl(null),
          pepStartDate: new FormControl(null),
          pepEndDate: new FormControl(null),
        },
          [Validators.required, dateRangeValidator])
      );
    } else {
      partner.removeControl('pepLegalRepresentative');
    }
  }
}
